import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { PhoneInput } from '../ui/PhoneInput';
import { AddressAutocomplete } from './AddressAutocomplete';

const formSchema = z
  .object({
    deliveryOption: z.enum(['pickup', 'delivery'], {
      required_error: 'Please select a delivery option',
    }),
    firstName: z.string().min(2, 'First name is required'),
    lastName: z.string().min(2, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    phone: z.string().min(10, 'Phone number is required'),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    prescriptionFile: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.deliveryOption === 'delivery') {
      if (!data.address || data.address.length < 5) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Address is required for delivery',
          path: ['address'],
        });
      }
      if (!data.city || data.city.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'City is required for delivery',
          path: ['city'],
        });
      }
      if (!data.state || data.state.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'State is required for delivery',
          path: ['state'],
        });
      }
      if (!data.postalCode || data.postalCode.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Postal code is required for delivery',
          path: ['postalCode'],
        });
      }
    }
  });

export type DeliveryFormValues = z.infer<typeof formSchema>;

interface DeliveryFormProps {
  form: UseFormReturn<DeliveryFormValues>;
  requiresPrescription?: boolean;
}

export function DeliveryForm({
  form,
  requiresPrescription,
}: DeliveryFormProps) {
  const deliveryOption = form.watch('deliveryOption');

  const handleAddressSelect = (addressComponents: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
  }) => {
    form.setValue('address', addressComponents.address, {
      shouldValidate: true,
    });
    form.setValue('city', addressComponents.city, { shouldValidate: true });
    form.setValue('state', addressComponents.state, { shouldValidate: true });
    form.setValue('postalCode', addressComponents.postalCode, {
      shouldValidate: true,
    });
  };

  return (
    <div className="space-y-3">
      <FormField
        control={form.control}
        name="deliveryOption"
        render={({ field }) => (
          <FormItem className="flex flex-col gap-[1px]">
            <FormLabel className="mb-0 leading-tight text-xs md:text-sm">
              Delivery Option
            </FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-row gap-8 md:gap-12 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="pickup"
                    id="pickup"
                    className="border-primary text-primary"
                  />
                  <Label
                    htmlFor="pickup"
                    className="text-xs md:text-sm text-gray-600 cursor-pointer"
                  >
                    Pickup
                  </Label>
                </div>
                <div className="flex items-center space-x-2 opacity-50">
                  <RadioGroupItem
                    value="delivery"
                    id="delivery"
                    disabled
                    className="border-gray-500 disabled:opacity-80 text-gray-300 disabled:cursor-not-allowed"
                  />
                  <Label
                    htmlFor="delivery"
                    className="text-xs md:text-sm text-gray-500 cursor-not-allowed"
                  >
                    Delivery (Coming Soon)
                  </Label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage className="mt-1 text-xs" />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[1px]">
              <FormLabel className="mb-0 leading-tight text-xs md:text-sm">
                First Name
              </FormLabel>
              <FormControl>
                <Input
                  className="rounded-xl border border-gray-200 px-3 py-2 text-xs md:text-sm text-gray-600 focus: ring-2 focus:ring-primary focus: outline-none w-full bg-white placeholder-gray-400 mt-0 mb-0"
                  placeholder="John"
                  {...field}
                />
              </FormControl>
              <FormMessage className="text-xs" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[1px]">
              <FormLabel className="mb-0 leading-tight text-xs md:text-sm">
                Last Name
              </FormLabel>
              <FormControl>
                <Input
                  className="rounded-xl border border-gray-200 px-3 py-2 text-xs md:text-sm text-gray-600 focus:ring-2 focus:ring-primary focus:outline-none w-full bg-white placeholder-gray-400 mt-0 mb-0"
                  placeholder="Doe"
                  {...field}
                />
              </FormControl>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[1px]">
              <FormLabel className="mb-0 leading-tight text-xs md:text-sm">
                Email
              </FormLabel>
              <FormControl>
                <Input
                  className="rounded-xl border border-gray-200 px-3 py-2 text-xs md:text-sm text-gray-600 focus:ring-2 focus:ring-primary focus:outline-none w-full bg-white placeholder-gray-400 mt-0 mb-0"
                  placeholder="john. doe@example.com"
                  {...field}
                />
              </FormControl>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[1px]">
              <FormLabel className="mb-0 leading-tight text-xs md:text-sm">
                Phone Number
              </FormLabel>
              <FormControl>
                <PhoneInput
                  value={field.value || '+234 '}
                  onChange={field.onChange}
                  className="w-full mt-0 mb-0"
                  placeholder="Enter your phone number"
                />
              </FormControl>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>
          )}
        />
      </div>

      {deliveryOption === 'delivery' && (
        <FormField
          control={form.control}
          name="address"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[1px]">
              <FormLabel className="mb-0 leading-tight text-xs md:text-sm">
                Delivery Address
              </FormLabel>
              <FormControl>
                <AddressAutocomplete
                  value={field.value || ''}
                  onChange={field.onChange}
                  onAddressSelect={handleAddressSelect}
                  className="rounded-xl border border-gray-200 px-3 py-2 text-xs md:text-sm text-gray-600 focus:ring-2 focus:ring-primary focus:outline-none w-full bg-white placeholder-gray-400 mt-0 mb-0"
                  placeholder="Start typing your address..."
                />
              </FormControl>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>
          )}
        />
      )}

      {requiresPrescription && (
        <FormField
          control={form.control}
          name="prescriptionFile"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[1px]">
              <FormLabel className="mb-0 leading-tight text-xs md:text-sm">
                Upload Prescription
              </FormLabel>
              <FormControl>
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  className="rounded-xl border border-gray-200 px-3 py-2 text-xs md:text-sm text-gray-600 focus:ring-2 focus: ring-primary focus:outline-none w-full bg-white mt-0 mb-0 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:bg-gray-100"
                  onChange={e =>
                    field.onChange(
                      e.target.files && e.target.files[0]
                        ? e.target.files[0]
                        : null
                    )
                  }
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage className="mt-1 text-xs" />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
